import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  Controller,
  useFieldArray,
  UseFieldArrayReturn,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import FormControlledDropdown from '../../../../components/inputs/FormInputs/FormControlledDropdown';
import FormControlledInput from '../../../../components/inputs/FormInputs/FormControlledInput';
import FormControlledNumberInput from '../../../../components/inputs/FormInputs/FormControlledNumberInput';
import {
  fetchAPEntryAttachment,
  fetchVendorTypes,
} from '../../../../services/accounting/ACFormsService';
import { APTransactionEntryForm } from '../../../../Interfaces/Accounting/ACForms.interfaces';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { fetchVendors } from '@/src/app/services/accounting/vendorsService';
import { getVendorKeyPerCompany } from '../../accountsPayables/apUtil';
import { getSource } from '@/src/utils/company';
import FormControlledCalendar from '@/src/app/components/inputs/FormInputs/FormControlledCalendar';
import { Button } from 'primereact/button';
import APEntryItem from './APEntryItem';
import SingleFileInput from '@/src/app/components/inputs/StyledInputs/SingleFileInput';
import { FILE_SIZE_LIMIT } from '@/src/utils/fileConsts';
import FormErrorMessage from '@/src/app/components/messages/FormErrorMessage';
import { downloadFile, transformBToMB } from '@/src/utils/fileUtil';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import ErrorToast, {
  showToast,
} from '@/src/app/components/messages/ErrorAlert';
import { APVendor } from '@/src/app/Interfaces/Accounting/AccountsPayables.interfaces';

type APTransactionFormProps = {
  fieldArray: UseFieldArrayReturn<APTransactionEntryForm, 'items'>;
  draftID?: string;
};

const APTransactionForm = ({ fieldArray, draftID }: APTransactionFormProps) => {
  const { selectedCompany } = useCompanyContext();
  const vendorKey = getVendorKeyPerCompany(getSource(selectedCompany));
  const {
    control,
    resetField,
    formState: { errors },
    setValue,
    reset,
    getValues,
  } = useFormContext<APTransactionEntryForm>();
  const { fields, append, remove } = fieldArray;
  const toast = useRef<Toast>(null);
  const [vendorType, prevAttachment, removed] =
    useWatch<APTransactionEntryForm>({
      name: ['vendorType', 'prevAttachment', 'removeAttachment'],
    });

  const vendorTypes = useQuery({
    queryKey: ['getVendorTypes'],
    queryFn: ({ signal }) => fetchVendorTypes(signal),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });

  const attachmentRequest = useMutation({
    mutationFn: () => fetchAPEntryAttachment(draftID ?? ''),
    onSuccess: (response) => {
      downloadFile(response, prevAttachment as string);
    },
    onError: () => {
      showToast(
        'error',
        toast,
        'AP Transaction Entry Attachment',
        "The attachment couldn't be downloaded",
        3000
      );
    },
  });

  const vendors = useQuery({
    queryKey: ['getAPEntryVendors', selectedCompany, vendorType],
    queryFn: ({ signal }) => {
      return fetchVendors(
        {
          company: selectedCompany!,
          active: true,
          types: [vendorType as string],
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && vendorType),
  });

  return (
    <div>
      <ErrorToast toastRef={toast} />
      <div className="grid mx-3 justify-content-center">
        <div className="col-12 sm:col-6">
          <label htmlFor="vendorType" className="text-standard block mb-2">
            Vendor Type
          </label>
          <FormControlledDropdown
            formID="vendorType"
            options={vendorTypes.data}
            labelField="Display"
            valueField="DBValue"
            placeholder={
              vendorTypes.isError
                ? 'Failed to load vendor types!'
                : 'Select Type'
            }
            isDisabled={vendorTypes.isError}
            isLoading={vendorTypes.isFetching}
            clearable={true}
            filter={true}
            resetFilterOnHide={true}
            rules={{
              required: {
                value: true,
                message: 'Vendor Type is a required field',
              },
            }}
            additionalChange={() => {
              resetField(`vendor`);
            }}
          />
        </div>
        <div className="col-12 sm:col-6">
          <label htmlFor="vendor" className="text-standard block mb-2">
            Vendor
          </label>
          <FormControlledDropdown
            formID="vendor"
            options={
              vendors.data?.map((vendor: APVendor) => ({
                ...vendor,
                [vendorKey]: `${vendor.code}`,
              })) ?? []
            }
            labelField="name"
            valueField={vendorKey}
            placeholder={
              vendors.isError ? 'Failed to load vendors!' : 'Select vendor'
            }
            isDisabled={vendors.isError}
            isLoading={vendors.isFetching}
            clearable={true}
            filter={true}
            resetFilterOnHide={true}
            rules={{
              required: {
                value: true,
                message: 'Vendor is a required field',
              },
            }}
          />
        </div>
        <div className="col-12 sm:col-6">
          <label htmlFor="number" className="text-standard block mb-2">
            Invoice Number
          </label>
          <FormControlledInput
            defaultValue=""
            formID="number"
            rules={{
              required: {
                value: true,
                message: 'Number is a required field',
              },
            }}
            autocomplete="off"
            maxLength={30}
          />
        </div>
        <div className="col-12 lg:col-6">
          <label htmlFor="date" className="text-standard block mb-2">
            Invoice Date
          </label>
          <FormControlledCalendar
            formID="date"
            readOnlyInput
            showButtonBar
            className="w-full"
            triggerOnChange={true}
            rules={{
              required: {
                value: true,
                message: 'Invoice Date is a required field',
              },
            }}
          />
        </div>
        <div className="col-12 sm:col-6">
          <label htmlFor="description" className="text-standard block mb-2">
            Invoice Description
          </label>
          <FormControlledInput
            defaultValue=""
            formID="description"
            rules={{
              required: {
                value: true,
                message: 'Description is a required field',
              },
            }}
            autocomplete="off"
            maxLength={30}
          />
        </div>
        <div className="col-12 sm:col-6">
          <label htmlFor="total" className="text-standard block mb-2">
            Invoice Total
          </label>
          <FormControlledNumberInput
            formID="total"
            mode="currency"
            currency="USD"
            locale="en-US"
            rules={{
              required: {
                value: true,
                message: 'Amount is a required field',
              },
            }}
          />
        </div>
        <div className="col-12">
          <label htmlFor="phase" className="text-standard block mb-2">
            Attachment
          </label>
          {prevAttachment && !removed && (
            <div>
              <div className="flex">
                The current attachment is:
                <div className="flex flex-column gap-2">
                  {attachmentRequest.isPending ? (
                    <ProgressSpinner className="w-1rem h-auto ml-4" />
                  ) : (
                    <span className="flex">
                      <span
                        className="ml-1 mr-2 totalColor cursor-pointer hover:underline"
                        onClick={() => {
                          if (draftID) attachmentRequest.mutate();
                        }}
                      >
                        {prevAttachment as string}
                      </span>
                      <i
                        onClick={() => {
                          setValue('removeAttachment', true, {
                            shouldDirty: true,
                          });
                        }}
                        className={`pi pi-ban errorText cursor-pointer my-auto`}
                      />
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-2 mb-1">
                To add a new attachment select it below:
              </div>
            </div>
          )}
          <Controller
            name="attachment"
            aria-label="file-input"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div>
                <SingleFileInput
                  value={value}
                  onChange={onChange}
                  acceptedFormats={['.pdf']}
                  totalLimit={FILE_SIZE_LIMIT}
                  totalLimitLabel={`${transformBToMB(FILE_SIZE_LIMIT)} MB`}
                  emptyMessage={`Upload attachment file. Allowed extensions: .pdf.`}
                  contentClassName={errors['attachment'] && 'border-red-500'}
                />
                {errors['attachment'] && (
                  <FormErrorMessage
                    errorMessage={errors['attachment'].message as string}
                  />
                )}
              </div>
            )}
          />
        </div>
        <div className="col-12 flex gap-3 align-content-center">
          <label className="font-bold text-20px my-auto">Items</label>
          <Button
            className="mr-auto text-17px py-2 px-3 font-bold"
            onClick={() => {
              reset(getValues(), { keepDirty: true, keepErrors: true });
              append({} as Parameters<typeof append>[0]);
            }}
            type="button"
          >
            +
          </Button>
        </div>
        <div className="col-12 grid gap-4">
          {fields?.map((field, index) => {
            return (
              <APEntryItem
                key={field.id}
                fieldIndex={index}
                remove={remove}
                fetch={!index}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default APTransactionForm;
